import React, { useEffect, useState, useCallback } from 'react';
import {useParams,useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api';
import { ActionButton, IconButton,CancelButton } from '../common/AdminButtons';
import { ButtonGroup } from '../common/AdminForm';
import { Table, Tr, Td, Th } from '../common/AdminTable';
import { ErrorMessage, LoadingMessage, SuccessMessage } from '../common/AdminMessages';
import { Modal } from '../../Modal';
import Word from '../../../models/Word';
import WordForm from './WordForm';
import WordUploadForm from './WordUploadForm';
import { apiSuccess } from '../../../utils/utils';

const ListContainer = styled.div`
    padding: 20px;
`;

interface WordListProps {
    jargonId: number;
}

const WordList: React.FC = () => {
    const { jargonId } = useParams<{ jargonId: string }>();
    const navigate = useNavigate();
    const [words, setWords] = useState<Word[]>([]);
    const [loading, setLoading] = useState(true);
    const [message,setMessage] = useState('');
    const [error, setError] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFileModalOpen,setIsFileModalOpen]=useState(false);
    const [selectedWord, setSelectedWord] = useState<Word | undefined>(undefined);
    // State to track invalid jargonId (for redirect)
    const [isInvalidJargon, setIsInvalidJargon] = useState(false);
    //see if valid jargonID was passed
    const nJargonId = jargonId?parseInt(jargonId):NaN;

    useEffect(() => {
        if(!jargonId || isNaN(nJargonId) || nJargonId<=0){
            console.error("Missing or invalid jargonId in URL");
            setIsInvalidJargon(true);
        } else {
            fetchWords(nJargonId);
        }
    }, [jargonId]);
    
    const editWord = useCallback((id: number) => {
        setSelectedWord(words.find(item => item.id === id));
        setIsModalOpen(true);
    }, [words]);
    const addWord = () =>{
        setSelectedWord(undefined); // Ensure we're adding a new word
        setIsModalOpen(true);
    };

    const deleteWord = useCallback(async (id: number, word: string) => {
        if (window.confirm(`Are you sure you want to delete the word "${word}"?`)) {
            console.log(`Deleting ${word}`);
            try {
                let response = await api.delete(`/halo/words/${id}`);
                console.log(`received response: ${response}`);
                console.log(`received response status ${response.status}`);
                if (response.status >=200 && response.status <300) {
                    fetchWords(nJargonId);
                } else {
                    alert(`HTTP ${response.status}: ${response.statusText}`);
                }
            } catch (error) {
                console.error("Error deleting word:", error);
                alert("Failed to delete the word.");
            }
        }
    }, [nJargonId]);
    
    const deleteWords = useCallback(async () =>{
        if(window.confirm("Are you sure you want to delete all the words in this jargon? Have you backed the up or something?"))
        {
            try{
                let response = await api.delete(`/halo/words/jargon/${nJargonId}`);
                if(apiSuccess(response)){
                    fetchWords(nJargonId);
                }
                else{
                    alert(`HTTP ${response.status}: ${response.statusText}`);
                }
            }
            catch(error)
            {
                console.error("Error deleting word:", error);
                setError("Error deleting word:" + error);
            }
        }
    },[nJargonId]);
    const openFileUploadModal = () =>
    {
        setIsFileModalOpen(true);
    }

    
    if (isInvalidJargon) {
        console.error("Missing or invalid jargonId in URL");
        navigate("/halo/jargons");
        return null;
    }

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const onModalSuccess = () => {
        setIsModalOpen(false);
        setLoading(true);
        fetchWords(nJargonId);
    };
    const closeFileModal = () => {
        setIsFileModalOpen(false);
    };

    //this should do something with the result, like list how many words were added.
    const onFileModalSuccess = (result:number) => {
        setIsFileModalOpen(false);
        setLoading(true);
        fetchWords(nJargonId);
    };

    const fetchWords = async (nJargonId:number) => {
        try {
            const response = await api.get(`/halo/words/jargon/${nJargonId}`);
            setWords(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch words');
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (loading) {
            return <LoadingMessage>Loading words...</LoadingMessage>;
        }

        return (
            <>
            {message && <SuccessMessage>{message}</SuccessMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <ActionButton onClick={addWord}>Add Word</ActionButton> <ActionButton onClick={openFileUploadModal}>Add Words (CSV)</ActionButton> <CancelButton onClick={deleteWords}>Delete All</CancelButton>
            <Table>
                <thead>
                    <Tr>
                        <Th>Edit</Th>
                        <Th>Word</Th>
                        <Th>Part of Speech</Th>
                    </Tr>
                </thead>
                <tbody>
                    {words.map(word => (
                        <Tr key={word.id}>
                            <Td>
                                <IconButton onClick={() => editWord(word.id)}>
                                    <i className="fa-sharp fa-solid fa-pen-to-square"></i>
                                </IconButton>
                                <IconButton onClick={() => deleteWord(word.id, word.word)}>
                                    <i className="fa-sharp fa-solid fa-trash"></i>
                                </IconButton>
                            </Td>
                            <Td>{word.word}</Td>
                            <Td>{word.partOfSpeech}</Td>
                        </Tr>
                    ))}
                </tbody>
            </Table>
            </>
        );
    };

    return (
        <ListContainer>
            <h1>Words</h1>
            {renderContent()}
            <Modal isOpen={isModalOpen} onClose={closeModal}>
                <WordForm onCancel={closeModal} onSuccess={onModalSuccess} jargonId={nJargonId}></WordForm>
            </Modal>
            <Modal isOpen={isFileModalOpen} onClose={closeFileModal}>
                <WordUploadForm onCancel={closeFileModal} onSuccess={onFileModalSuccess} jargonId={nJargonId}></WordUploadForm>
            </Modal>
        </ListContainer>
    );
};

export default WordList;
